import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Upload, FileText } from "lucide-react";
import api from "@/lib/api";
import { downloadBlob, errMsg } from "@/lib/customerApi";

function Slot({ s, reload }) {
  const [providers, setProviders] = useState(3);
  const builtin = s.slot !== "sample_report";
  const getBlank = () => downloadBlob(api, `/admin/templates/builtin/${s.slot}?providers=${providers}`, `DYOR-Power-Study-${s.label.replace(/ /g, "-")}-Template.pdf`)
    .catch(() => toast.error("Download failed"));
  const up = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/templates/${s.slot}`, fd); toast.success("Template saved"); reload(); }
    catch (err) { toast.error(errMsg(err, "Upload failed")); }
  };
  const del = async (f) => { await api.delete(`/admin/templates/files/${f.id}`); reload(); };
  return (
    <div className="card p-5" data-testid={`template-slot-${s.slot}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <FileText className="text-[#84CC16] shrink-0" size={20} />
          <div>
            <p className="text-white font-semibold">{s.label}</p>
            <p className="text-slate-400 text-sm">{s.description}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {builtin && s.slot === "comparison_table" && (
            <label className="text-xs text-slate-400 flex items-center gap-1.5">Providers
              <input data-testid="template-provider-count" type="number" min={1} max={8} value={providers}
                onChange={(e) => setProviders(Math.max(1, Math.min(8, Number(e.target.value) || 1)))}
                className="w-14 bg-[#0B0F17] border border-white/15 rounded px-2 py-1.5 text-white" />
            </label>
          )}
          {builtin && (
            <button data-testid={`template-builtin-${s.slot}`} onClick={getBlank}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-[#F59E0B] text-[#0B1F3A] font-semibold">
              <Download size={14} /> Fillable PDF
            </button>
          )}
          <label className="cursor-pointer inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-[#2563EB] text-white">
            <Upload size={14} /> {builtin ? "Upload my version" : "Upload / replace"}
            <input data-testid={`template-upload-${s.slot}`} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" className="hidden" onChange={up} />
          </label>
        </div>
      </div>
      <div className="mt-4 space-y-1.5">
        {s.files.length === 0 && <p className="text-slate-500 text-sm italic" data-testid={`template-empty-${s.slot}`}>{builtin ? "Using the built-in DYOR template. Upload your own version to keep a copy here." : "No file uploaded yet."}</p>}
        {s.files.map((f) => (
          <div key={f.id} className="flex items-center justify-between gap-2 text-sm bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2">
            <span className="text-slate-300 truncate"><span className="font-mono text-[10px] uppercase text-[#84CC16] mr-2">{f.ext}</span>{f.filename}</span>
            <span className="flex gap-3 shrink-0">
              <button data-testid={`template-download-${f.id}`} onClick={() => downloadBlob(api, `/admin/templates/files/${f.id}`, f.filename)} className="text-[#3B82F6]"><Download size={15} /></button>
              <button data-testid={`template-delete-${f.id}`} onClick={() => del(f)} className="text-red-400"><Trash2 size={15} /></button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TemplatesAdmin() {
  const [slots, setSlots] = useState(null);
  const load = useCallback(() => api.get("/admin/templates").then((r) => setSlots(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!slots) return <p className="text-slate-400 font-mono">Loading…</p>;
  return (
    <div data-testid="admin-templates">
      <h1 className="font-display font-bold text-2xl text-white">Report templates</h1>
      <p className="text-slate-400 text-sm mt-1 mb-6">Download the built-in fillable PDFs and type straight into the fields in Acrobat or your browser. For Provider Comparison, set how many provider columns you need (1–8). You can also upload your own PDF, Word, or Excel versions.</p>
      <div className="space-y-4">{slots.map((s) => <Slot key={s.slot} s={s} reload={load} />)}</div>
    </div>
  );
}
